import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Stethoscope, Baby, Milk, Droplet, Activity, Syringe, Moon, Salad, Thermometer, HeartHandshake,
  MessageCircle, ShoppingBag, Play, Shield, Sparkles, CheckCircle2, Instagram, Youtube, Phone
} from "lucide-react";

const HIGHLIGHTS = [
  { icon: Baby, label: "Newborn Care", color: "#CBE4E8" },
  { icon: Milk, label: "Feeding", color: "#FFD6C2" },
  { icon: Droplet, label: "Wet Diapers", color: "#CBE4E8" },
  { icon: Activity, label: "Stool Guide", color: "#FFD6C2" },
  { icon: Sparkles, label: "Growth", color: "#CBE4E8" },
  { icon: Syringe, label: "Vaccines", color: "#FFD6C2" },
  { icon: Moon, label: "Sleep", color: "#CBE4E8" },
  { icon: Salad, label: "Nutrition", color: "#FFD6C2" },
  { icon: Thermometer, label: "Fever", color: "#CBE4E8" },
  { icon: HeartHandshake, label: "Parenting Tips", color: "#FFD6C2" },
];

const TRUST = [
  { icon: Stethoscope, title: "Pediatrician & Neonatologist", sub: "MD, RMC No. 34455" },
  { icon: CheckCircle2, title: "Evidence-based guidance", sub: "Practical, safe, current" },
  { icon: HeartHandshake, title: "Parent-friendly", sub: "Simple, warm explanations" },
  { icon: Shield, title: "Secure teleconsultation", sub: "Private & encrypted" },
];

const FAQ = [
  { q: "Is this a real doctor consultation?", a: "Yes. Every consultation is with Dr. Hariom Choudhary, a qualified Pediatrician & Neonatologist." },
  { q: "What if my baby needs emergency care?", a: "Teleconsultation is NOT for emergencies. Please rush to the nearest hospital for red-flag symptoms." },
  { q: "Do you consult in Hindi?", a: "Yes, Dr. Hariom consults in Hindi and English both. Use the language toggle in the top bar." },
  { q: "How do I make payment?", a: "Currently we use UPI (drhariom09999@oksbi). Upload payment screenshot to confirm your booking." },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-10 -left-20 w-72 h-72 rounded-full" style={{background:"radial-gradient(circle,#CBE4E8 0%,transparent 70%)"}}/>
          <div className="absolute -bottom-20 right-0 w-96 h-96 rounded-full" style={{background:"radial-gradient(circle,#FFD6C2 0%,transparent 70%)"}}/>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="chip mb-4" data-testid="hero-tagline"><Sparkles size={13}/> Make Parenting Joyful</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{color:"#232F2F"}}>
              Trusted Pediatric<br/>
              <span style={{color:"#2D7A78"}}>Teleconsultation</span> &<br/>
              Parenting Guidance
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#5A6D6D] max-w-xl">
              With <span className="font-semibold text-[#232F2F]">Dr. Hariom Choudhary</span> — Pediatrician & Neonatologist. Evidence-based, parent-friendly, and available in English & Hindi.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book" className="btn-primary" data-testid="hero-book-btn"><Stethoscope size={18} strokeWidth={1.8}/> Book Consultation</Link>
              <a href="https://wa.me/916350018891" target="_blank" rel="noreferrer" className="btn-outline" data-testid="hero-wa-btn"><MessageCircle size={18} strokeWidth={1.8}/> Chat on WhatsApp</a>
              <Link to="/ebooks" className="btn-outline" data-testid="hero-ebooks-btn"><ShoppingBag size={18} strokeWidth={1.8}/> Buy E-Books</Link>
              <Link to="/content" className="btn-outline" data-testid="hero-videos-btn"><Play size={18} strokeWidth={1.8}/> Watch Videos</Link>
            </div>
          </div>

          <div className="md:col-span-5 fade-up">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] -z-10" style={{background:"linear-gradient(135deg,#CBE4E8,#FFD6C2)"}}/>
              <img src="https://images.pexels.com/photos/5888160/pexels-photo-5888160.jpeg" alt="Dr. Hariom" className="rounded-[2rem] w-full aspect-[4/5] object-cover shadow-xl" data-testid="hero-image"/>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-3 shadow-lg flex items-center gap-2 border border-[#E5EBEB]">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{background:"#CBE4E8"}}>
                  <Stethoscope size={18} color="#2D7A78" strokeWidth={1.8}/>
                </div>
                <div>
                  <div className="text-xs text-[#5A6D6D]">Verified</div>
                  <div className="text-sm font-semibold" style={{color:"#232F2F"}}>Pediatrician & Neonatologist</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="overline mb-3">Care Categories</div>
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6" style={{color:"#232F2F"}}>Everything a new parent needs</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {HIGHLIGHTS.map((h) => {
            const Icon = h.icon;
            return (
              <div key={h.label} className="card-soft flex flex-col items-start gap-2" data-testid={`highlight-${h.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{background: h.color}}>
                  <Icon size={22} color="#24615F" strokeWidth={1.6}/>
                </div>
                <div className="font-semibold text-sm">{h.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {TRUST.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="card-soft" data-testid={`trust-${t.title.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}>
                <Icon size={22} color="#2D7A78" strokeWidth={1.7}/>
                <div className="mt-3 font-semibold text-[15px]">{t.title}</div>
                <div className="text-sm text-[#5A6D6D]">{t.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* VIDEOS placeholder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline mb-1">Watch & Learn</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl" style={{color:"#232F2F"}}>Latest videos & reels</h2>
          </div>
          <a href="https://youtube.com/@drhariompediatrician" target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#2D7A78]">YouTube ↗</a>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {t:"Newborn care tips every parent must know", url:"https://www.youtube.com/embed/dQw4w9WgXcQ"},
            {t:"Feeding & gas relief guide", url:"https://www.youtube.com/embed/dQw4w9WgXcQ"},
            {t:"When fever needs urgent care", url:"https://www.youtube.com/embed/dQw4w9WgXcQ"},
          ].map((v,i) => (
            <div key={i} className="card-soft p-0 overflow-hidden" data-testid={`video-card-${i}`}>
              <div className="aspect-video bg-[#F0F3F3] flex items-center justify-center">
                <Play size={40} color="#2D7A78"/>
              </div>
              <div className="p-4">
                <div className="font-semibold text-sm">{v.t}</div>
                <div className="text-xs text-[#5A6D6D] mt-1">Dr. Hariom · YouTube</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="overline mb-1">Parents love us</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6" style={{color:"#232F2F"}}>What parents say</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <div key={t.id} className="card-soft" data-testid={`testimonial-${t.id}`}>
                <div className="text-[#FFC24C] mb-2">{"★".repeat(t.rating || 5)}</div>
                <p className="text-sm text-[#232F2F]">“{t.text}”</p>
                <div className="mt-3 text-xs text-[#5A6D6D]">— {t.name}, {t.location}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="overline mb-1">FAQ</div>
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6" style={{color:"#232F2F"}}>Common questions</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="card-soft" data-testid={`faq-${i}`}>
              <summary className="font-semibold cursor-pointer">{f.q}</summary>
              <p className="mt-2 text-sm text-[#5A6D6D]">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-[2rem] p-8 md:p-12 text-white relative overflow-hidden" style={{background:"linear-gradient(135deg,#2D7A78,#24615F)"}} data-testid="home-cta">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-widest uppercase opacity-80">Ready when you are</div>
            <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl mt-2 leading-tight">Book a consultation with Dr. Hariom — from home.</h3>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/book" className="bg-white text-[#2D7A78] font-semibold px-6 py-3 rounded-full">Book Now</Link>
              <a href="https://wa.me/916350018891" target="_blank" rel="noreferrer" className="border border-white/70 text-white px-6 py-3 rounded-full">WhatsApp</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
